const PORTAL_BANNERS_KEY = 'nog.portal.banners';
const PORTAL_BANNERS_EVENT = 'nog:portal-banners:changed';

export type PortalBannerVariant = 'info' | 'warning' | 'success' | 'danger';
export type PortalBannerAudience = 'all' | 'tenant-admin' | 'tenant-user';

export interface PortalBannerItem {
  id: string;
  message: string;
  title?: string;
  variant?: PortalBannerVariant;
  dismissible?: boolean;
  audience?: PortalBannerAudience;
}

export interface PortalBannerScope {
  enabled: boolean;
  text: string;
  title?: string;
  variant?: PortalBannerVariant;
  dismissible?: boolean;
}

export interface PortalBannerConfig {
  global: PortalBannerScope;
  tenants: Record<string, PortalBannerScope>;
  updatedAtUtc?: string;
}

function isPortalBannerItem(value: unknown): value is PortalBannerItem {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const item = value as Record<string, unknown>;
  return typeof item.id === 'string' && typeof item.message === 'string';
}

function normalizeTenantKey(value: string): string {
  return String(value || '').trim().toLowerCase();
}

function normalizeScope(value: unknown, fallbackTitle: string): PortalBannerScope {
  const input = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const variant = input.variant;
  const safeVariant: PortalBannerVariant = variant === 'warning' || variant === 'success' || variant === 'danger' ? variant : 'info';
  return {
    enabled: input.enabled === true,
    text: typeof input.text === 'string' ? input.text : '',
    title: typeof input.title === 'string' ? input.title : fallbackTitle,
    variant: safeVariant,
    dismissible: input.dismissible !== false,
  };
}

export function createDefaultBannerScope(title: string): PortalBannerScope {
  return {
    enabled: false,
    text: '',
    title,
    variant: 'info',
    dismissible: true,
  };
}

const DEFAULT_CONFIG: PortalBannerConfig = {
  global: createDefaultBannerScope('Platform Notice'),
  tenants: {},
};

export function loadPortalBannerConfig(): PortalBannerConfig {
  try {
    const raw = localStorage.getItem(PORTAL_BANNERS_KEY);
    if (!raw) {
      return { ...DEFAULT_CONFIG };
    }

    const parsed = JSON.parse(raw);

    // Backward-compatibility: previous model was an array of banner items.
    if (Array.isArray(parsed)) {
      const legacyItems = parsed.filter(isPortalBannerItem);
      const first = legacyItems[0];
      if (!first) {
        return { ...DEFAULT_CONFIG };
      }
      return {
        global: {
          enabled: true,
          text: first.message,
          title: first.title || 'Platform Notice',
          variant: first.variant || 'info',
          dismissible: first.dismissible !== false,
        },
        tenants: {},
      };
    }

    const data = (parsed && typeof parsed === 'object' ? parsed : {}) as Record<string, unknown>;
    const tenantsObj = (data.tenants && typeof data.tenants === 'object' ? data.tenants : {}) as Record<string, unknown>;
    const normalizedTenants: Record<string, PortalBannerScope> = {};
    Object.entries(tenantsObj).forEach(([key, value]) => {
      const normalizedKey = normalizeTenantKey(key);
      if (!normalizedKey) {
        return;
      }
      normalizedTenants[normalizedKey] = normalizeScope(value, 'Tenant Notice');
    });

    return {
      global: normalizeScope(data.global, 'Platform Notice'),
      tenants: normalizedTenants,
      updatedAtUtc: typeof data.updatedAtUtc === 'string' ? data.updatedAtUtc : undefined,
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

export function savePortalBannerConfig(config: PortalBannerConfig): void {
  const normalized: PortalBannerConfig = {
    global: normalizeScope(config.global, 'Platform Notice'),
    tenants: {},
    updatedAtUtc: new Date().toISOString(),
  };

  Object.entries(config.tenants || {}).forEach(([key, value]) => {
    const normalizedKey = normalizeTenantKey(key);
    if (!normalizedKey) {
      return;
    }
    normalized.tenants[normalizedKey] = normalizeScope(value, 'Tenant Notice');
  });

  localStorage.setItem(PORTAL_BANNERS_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent(PORTAL_BANNERS_EVENT, { detail: normalized }));
}

export function resolvePortalBanners(
  config: PortalBannerConfig,
  context: { tenantSlug?: string; tenantId?: string; audience: PortalBannerAudience },
): PortalBannerItem[] {
  const items: PortalBannerItem[] = [];

  if (config.global.enabled && config.global.text.trim().length > 0) {
    const globalMessage = config.global.text.trim().length > 0
      ? config.global.text
      : 'Platform status update in effect.';
    items.push({
      id: 'global-banner',
      title: config.global.title || 'Platform Notice',
      message: globalMessage,
      variant: config.global.variant || 'info',
      dismissible: config.global.dismissible !== false,
      audience: 'all',
    });
  }

  const tenantSlugKey = normalizeTenantKey(context.tenantSlug || '');
  const tenantIdKey = normalizeTenantKey(context.tenantId || '');
  const tenantScope =
    (tenantSlugKey ? config.tenants[tenantSlugKey] : undefined) ||
    (tenantIdKey ? config.tenants[tenantIdKey] : undefined);
  const tenantKey = tenantSlugKey || tenantIdKey;
  if (tenantScope?.enabled) {
    const tenantMessage = tenantScope.text.trim().length > 0
      ? tenantScope.text
      : 'Tenant status update in effect.';
    items.push({
      id: `tenant-banner-${tenantKey}`,
      title: tenantScope.title || 'Tenant Notice',
      message: tenantMessage,
      variant: tenantScope.variant || 'info',
      dismissible: tenantScope.dismissible !== false,
      audience: context.audience,
    });
  }

  return items;
}

export function onPortalBannersChanged(handler: (config: PortalBannerConfig) => void): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<PortalBannerConfig | undefined>).detail;
    if (!detail || typeof detail !== 'object') {
      handler(loadPortalBannerConfig());
      return;
    }
    handler(loadPortalBannerConfig());
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key && event.key !== PORTAL_BANNERS_KEY) {
      return;
    }
    handler(loadPortalBannerConfig());
  };

  window.addEventListener(PORTAL_BANNERS_EVENT, listener as EventListener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(PORTAL_BANNERS_EVENT, listener as EventListener);
    window.removeEventListener('storage', onStorage);
  };
}
